import type { ThreeEvent } from '@react-three/fiber'
import { useAppSelector } from 'common/hooks/redux'
import { ElementIdLabel } from 'features/Procedure/procedure-model/element-id-label'
import type { DrawingElementDescription } from 'features/Procedure/state/procedure.type'
import { formatDrawingNoteDisplay } from 'features/Procedure/utils/drawingDescription.utils'
import React, { useMemo, useRef } from 'react'
import * as THREE from 'three'
import { type Vector3Like } from 'three'
import { materialCache } from './material-cache'
import { createPickGeometry, getCachedSurfaceFollowingCurve } from './utils'

type Props = {
  points: Vector3Like[]
  color: string
  lineWidth?: number
  bodyMesh?: THREE.Object3D
  cameraPosition?: THREE.Vector3
  isSelected?: boolean
  isFromSelectedProcedure?: boolean
  showElementIds?: boolean
  onClick?: (event: ThreeEvent<PointerEvent>) => void
  onPointerDown?: (event: ThreeEvent<PointerEvent>) => void
  onPointerEnter?: (event: ThreeEvent<PointerEvent>) => void
  onPointerOver?: (event: ThreeEvent<PointerEvent>) => void
  description?: DrawingElementDescription
  descriptionPosition?: Vector3Like
  onLabelClick?: (event: ThreeEvent<PointerEvent>) => void
  isPreview?: boolean
  useDepthTest?: boolean
  inactiveOpacity?: number
  readOnly?: boolean
}

export const FreeLine = React.memo(
  ({
    points,
    color,
    lineWidth = 3,
    bodyMesh,
    cameraPosition,
    isSelected = false,
    isFromSelectedProcedure = false,
    showElementIds: showElementIdsOverride,
    onClick,
    onPointerDown,
    onPointerEnter,
    onPointerOver,
    description,
    descriptionPosition,
    onLabelClick,
    isPreview = false,
    useDepthTest = false,
    inactiveOpacity = 0,
    readOnly = false,
  }: Props) => {
    const lineRef = useRef<THREE.Mesh>(null)
    const pickRef = useRef<THREE.Mesh>(null)
    const storeShowElementIds = useAppSelector((state) => state.procedureReducer.showElementIds)
    const isPrinting = useAppSelector((state) => state.procedureReducer.isPrinting)
    const showElementIds = showElementIdsOverride ?? storeShowElementIds

    const descriptionText = useMemo(() => formatDrawingNoteDisplay(description), [description])

    const vectorPoints = useMemo(
      () => points.map((p) => new THREE.Vector3(p.x, p.y, p.z)),
      [points],
    )

    const curve = useMemo(
      () => {
        if (vectorPoints.length < 2) return null
        return getCachedSurfaceFollowingCurve(points, vectorPoints, bodyMesh, cameraPosition)
      },
      // cameraPosition intentionally omitted from deps: projection is cached per
      // (points, bodyMesh). Re-projecting on every orbit was the perf bug we fixed.
      [points, vectorPoints, bodyMesh],
    )

    const midPoint = useMemo(() => {
      if (curve) {
        return curve.getPointAt(0.5)
      }
      return vectorPoints.length > 0 ? vectorPoints[0].clone() : new THREE.Vector3()
    }, [curve, vectorPoints])

    const labelPosition = useMemo(() => {
      if (descriptionPosition) {
        return new THREE.Vector3(
          descriptionPosition.x,
          descriptionPosition.y,
          descriptionPosition.z,
        )
      }
      return midPoint
    }, [descriptionPosition, midPoint])

    React.useEffect(() => {
      if (lineRef.current && curve) {
        if (lineRef.current.geometry) {
          lineRef.current.geometry.dispose()
        }

        // Read-only views (e.g. previous-visits comparison) use lower-poly tubes
        // since the line is never hovered/edited and resolution savings stack across
        // many drawings on two bodies.
        const tubularSegments = readOnly
          ? Math.min(vectorPoints.length, 24)
          : Math.min(vectorPoints.length * 2, 64)
        const radialSegments = readOnly ? 4 : 6
        const geometry = new THREE.TubeGeometry(
          curve,
          tubularSegments,
          lineWidth / 100,
          radialSegments,
          false,
        )
        lineRef.current.geometry = geometry
        geometry.computeBoundingSphere()
        lineRef.current.matrixAutoUpdate = false
        lineRef.current.updateMatrix()

        if (!readOnly && pickRef.current) {
          if (pickRef.current.geometry) {
            pickRef.current.geometry.dispose()
          }

          const pickGeometry = createPickGeometry(curve, lineWidth, false)
          pickRef.current.geometry = pickGeometry
          pickGeometry.computeBoundingSphere()
          pickRef.current.matrixAutoUpdate = false
          pickRef.current.updateMatrix()
        }
      }

      return () => {
        if (lineRef.current?.geometry) {
          lineRef.current.geometry.dispose()
        }
        if (pickRef.current?.geometry) {
          pickRef.current.geometry.dispose()
        }
      }
    }, [curve, vectorPoints.length, lineWidth, useDepthTest, readOnly])

    React.useEffect(() => {
      if (lineRef.current) {
        const material = materialCache.getLineMaterial(
          color,
          isSelected,
          isFromSelectedProcedure,
          isPrinting,
          isPreview,
          useDepthTest,
          inactiveOpacity,
        )
        lineRef.current.material = material
      }

      if (!readOnly && pickRef.current) {
        const pickMaterial = materialCache.getPickMaterial()
        pickRef.current.material = pickMaterial
      }
    }, [
      color,
      isSelected,
      isFromSelectedProcedure,
      isPrinting,
      isPreview,
      useDepthTest,
      inactiveOpacity,
      readOnly,
    ])

    const handleClick = (event: ThreeEvent<PointerEvent>) => {
      if (onClick) {
        event.stopPropagation()
        onClick(event)
      }
    }

    const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
      onPointerDown?.(event)
    }

    const handlePointerEnter = (event: ThreeEvent<PointerEvent>) => {
      onPointerEnter?.(event)
    }

    const handlePointerOver = (event: ThreeEvent<PointerEvent>) => {
      onPointerOver?.(event)
    }

    return (
      <>
        {!readOnly && (
          <mesh
            ref={pickRef}
            onClick={handleClick}
            onPointerDown={handlePointerDown}
            onPointerEnter={handlePointerEnter}
            onPointerOver={handlePointerOver}
          />
        )}
        <mesh
          ref={lineRef}
          onClick={readOnly ? undefined : handleClick}
          onPointerDown={readOnly ? undefined : handlePointerDown}
          onPointerEnter={readOnly ? undefined : handlePointerEnter}
          onPointerOver={readOnly ? undefined : handlePointerOver}
        />
        {showElementIds && isFromSelectedProcedure && descriptionText && labelPosition && (
          <ElementIdLabel
            text={descriptionText}
            position={labelPosition}
            color={color}
            useDepthTest={false}
            onClick={onLabelClick ?? onClick}
            onPointerDown={onPointerDown}
            onPointerEnter={onPointerEnter}
            onPointerOver={onPointerOver}
            offset={{
              x:
                Math.abs(labelPosition.x) > Math.abs(labelPosition.z)
                  ? labelPosition.x > 0
                    ? 1
                    : -1
                  : 0,
              z:
                Math.abs(labelPosition.z) >= Math.abs(labelPosition.x)
                  ? labelPosition.z >= 0
                    ? 1
                    : -1
                  : 0,
            }}
          />
        )}
      </>
    )
  },
)
